import { useState } from "react";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { useAdminView } from "@shared/client/admin-view";
import { Link } from "@shared/client/navigation";
import { usePageTitle } from "@shared/client/view";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel, PanelLink, StatCard, StatGroup } from "@shared/components/blocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import type { ClientListItem, LogRecord } from "@shared/lib/admin-db";
import type { AdminMetrics } from "@shared/lib/admin-metrics";
import { formatCents, formatDateTime, formatNumber, formatPercent } from "@shared/lib/format";

const CLIENT_STATUS_LABELS: Record<string, string> = {
  lead: "Prospect",
  trial: "Essai",
  active: "Actif",
  churned: "Perdu",
};

type OverviewData = { metrics: AdminMetrics; clients: ClientListItem[]; errors: LogRecord[] };

export default function AdminOverviewPage() {
  usePageTitle("Administration — Hune");
  const { data, error: loadError } = useAdminView<OverviewData>("/api/views/admin/overview");
  const [today] = useState(() =>
    new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date()),
  );
  if (!data && !loadError) return null;
  const metrics = data?.metrics ?? null;
  const clients = data?.clients ?? [];
  const errors = data?.errors ?? [];

  return (
    <>
      <AdminMeta note="30 derniers jours">{today}</AdminMeta>

      {loadError ? (
        <AdminDataNotice error={loadError} />
      ) : (
        <>
          <StatGroup columns={4}>
            <StatCard
              label="Clients actifs"
              note={`${formatNumber(metrics!.billing.trialingClients)} en essai`}
              value={formatNumber(metrics!.billing.activeClients)}
            />
            <StatCard
              label="MRR"
              note={`ARPA ${formatCents(metrics!.billing.arpaCents)}`}
              tint="teal"
              value={formatCents(metrics!.billing.mrrCents)}
            />
            <StatCard
              label="Visiteurs landing"
              note={`Taux CTA ${formatPercent(metrics!.ctaRate)}`}
              tint="amber"
              value={formatNumber(metrics!.funnel[0]?.uniques ?? 0)}
            />
            <StatCard
              label="Complétion onboarding"
              note={`Churn mensuel ${formatPercent(metrics!.billing.monthlyChurnRate)}`}
              tint="green"
              value={formatPercent(metrics!.onboardingCompletionRate)}
            />
          </StatGroup>

          <section className="mt-4.5 grid gap-4.5 xl:grid-cols-2">
            <AdminPanel action={<PanelLink href="/admin/clients">Tout voir</PanelLink>} flush title="Derniers clients">
              {clients.length === 0 ? (
                <AdminEmpty>
                  Aucun client pour le moment. Les comptes se créent automatiquement à la fin de l’onboarding.
                </AdminEmpty>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="pl-5">Client</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="max-sm:hidden">Utilisateurs</TableHead>
                      <TableHead className="pr-5 text-right">MRR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.slice(0, 6).map((client) => (
                      <TableRow key={client.id}>
                        <TableCell className="pl-5">
                          <Link className="font-semibold hover:text-foreground" href={`/admin/clients/${client.id}`}>
                            {client.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <AdminChip tone={client.status}>
                            {CLIENT_STATUS_LABELS[client.status] ?? client.status}
                          </AdminChip>
                        </TableCell>
                        <TableCell className="tabular-nums max-sm:hidden">{client.member_count}</TableCell>
                        <TableCell className="pr-5 text-right tabular-nums">
                          {formatCents(client.subscription?.status === "active" ? client.subscription.mrr_cents : 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AdminPanel>

            <AdminPanel action={<PanelLink href="/admin/logs?level=error">Tous les logs</PanelLink>} flush title="Dernières erreurs">
              {errors.length === 0 ? (
                <AdminEmpty>Aucune erreur enregistrée récemment. 🎉</AdminEmpty>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="pl-5">Quand</TableHead>
                      <TableHead>Opération</TableHead>
                      <TableHead className="pr-5">Erreur</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="pl-5 whitespace-nowrap text-muted-foreground tabular-nums">
                          {formatDateTime(log.created_at)}
                        </TableCell>
                        <TableCell><code className="font-mono text-[12px]">{log.operation}</code></TableCell>
                        <TableCell className="pr-5 text-destructive">{log.error || `HTTP ${log.status ?? "—"}`}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AdminPanel>
          </section>
        </>
      )}
    </>
  );
}
